import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Trash2, User, Home, Users, ArrowRightLeft, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PhoneField from "@/components/PhoneField";
import Timeline from "@/components/Timeline";
import PropertyDossier from "@/components/PropertyDossier";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLang } from "@/context/LanguageContext";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";

const SOURCES = ["website", "referral", "portal", "social", "walk_in", "phone", "other"];
const PRIORITIES = ["low", "medium", "high"];

// Colour treatment for the 13 pipeline stages — matches the vendor pipeline vibe.
const STAGE_STYLE = {
  new_vendor_lead: "bg-stone-100 text-stone-700",
  contact_made: "bg-blue-50 text-blue-700",
  valuation_booked: "bg-amber-50 text-amber-700",
  valuation_completed: "bg-amber-50 text-amber-700",
  awaiting_decision: "bg-purple-50 text-purple-700",
  mandate_signed: "bg-emerald-50 text-emerald-700",
  closed_won: "bg-emerald-100 text-emerald-800",
  property_live: "bg-teal-50 text-teal-700",
  viewings: "bg-purple-50 text-purple-700",
  offer_received: "bg-orange-50 text-orange-700",
  under_offer: "bg-orange-100 text-orange-800",
  sold: "bg-emerald-100 text-emerald-800",
  closed_lost: "bg-stone-100 text-stone-400 line-through",
};

const PRIORITY_STYLE = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-stone-50 text-stone-700 border-stone-200",
  low: "bg-stone-50 text-stone-500 border-stone-200",
};

const emptyLead = () => ({
  name: "", source: "website", stage: "new_vendor_lead", priority: "medium",
  email: "", phone: "", address: "",
  buyer_id: "", seller_id: "", property_id: "",
  next_action: "", next_follow_up_date: "", notes: "",
});

export default function Leads() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, b, s, p, ps] = await Promise.all([
        api.get("/leads", { params: search ? { search } : {} }),
        api.get("/buyers"), api.get("/sellers"), api.get("/properties"),
        api.get("/pipeline/stages"),
      ]);
      setItems(l.data); setBuyers(b.data); setSellers(s.data); setProperties(p.data);
      setPipelineStages(ps.data.stages || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [search]);

  const stageLabel = useCallback((key) => {
    const s = pipelineStages.find((x) => x.key === key);
    if (!s) return key;
    return lang === "fr" ? s.label_fr : s.label_en;
  }, [pipelineStages, lang]);

  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const filtered = useMemo(() => {
    return items.filter((l) => {
      if (stageFilter !== "all" && l.stage !== stageFilter) return false;
      if (priorityFilter !== "all" && l.priority !== priorityFilter) return false;
      return true;
    });
  }, [items, stageFilter, priorityFilter]);

  const save = async (payload) => {
    if (!payload.name) return toast.error(t("leadNameRequired"));
    try {
      if (payload.buyer_id) payload.buyer_name = buyers.find((b) => b.id === payload.buyer_id)?.name || "";
      if (payload.seller_id) payload.seller_name = sellers.find((s) => s.id === payload.seller_id)?.name || "";
      if (payload.property_id) payload.property_name = properties.find((p) => p.id === payload.property_id)?.name || "";
      if (payload.id) await api.put(`/leads/${payload.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(t("saved"));
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/leads/${id}`); toast.success(t("deleted")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const quickUpdate = async (id, patch) => {
    try { await api.put(`/leads/${id}`, patch); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const convert = async (lead, target) => {
    const confirmMsg = target === "buyer" ? t("confirmConvertToBuyer") : t("confirmConvertToSeller");
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/leads/${lead.id}/convert`, { target });
      toast.success(target === "buyer" ? t("convertedToBuyer") : t("convertedToSeller"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-stone-900">{t("leadsTitle")}</h1>
          <p className="text-sm text-stone-500 mt-2">{t("leadsSubtitle")}</p>
        </div>
        <Button data-testid="add-lead-button" onClick={() => setDialog(emptyLead())} className="bg-stone-900 hover:bg-stone-800 text-white">
          <Plus size={16} className="mr-1.5" /> {t("addLead")}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="leads-search" placeholder={t("leadsSearchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-40" data-testid="leads-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allStages")}</SelectItem>
            {pipelineStages.map((s) => <SelectItem key={s.key} value={s.key}>{stageLabel(s.key)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40" data-testid="leads-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allPriorities")}</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`priority_${p}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-2xl">{t("noLeads")}</div>
      )}

      <div className="space-y-2">
        {filtered.map((lead) => (
          <div key={lead.id} data-testid={`lead-row-${lead.id}`} className="group bg-white border border-stone-200 rounded-xl p-4 hover:border-stone-400 transition-colors">
            <div className="flex items-start gap-4 flex-wrap">
              <button onClick={() => setDialog({ ...lead })} className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-base font-medium text-stone-900 truncate">{lead.name}</p>
                  <Badge className={`text-[10px] uppercase ${PRIORITY_STYLE[lead.priority || "medium"]}`}>{t(`priority_${lead.priority || "medium"}`)}</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1 text-[11px] text-stone-500">
                  {lead.source && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5">{t(`source_${lead.source}`)}</span>}
                  {lead.buyer_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><User size={10} />{lead.buyer_name}</span>}
                  {lead.seller_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><Users size={10} />{lead.seller_name}</span>}
                  {lead.property_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><Home size={10} />{lead.property_name}</span>}
                </div>
                {lead.next_action && <p className="text-xs text-stone-600 mt-1.5">↳ {lead.next_action}{lead.next_follow_up_date && <span className="text-stone-400"> · {fmtDate(lead.next_follow_up_date)}</span>}</p>}
              </button>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                <Select value={lead.stage} onValueChange={(v) => quickUpdate(lead.id, { stage: v })}>
                  <SelectTrigger className={`h-8 w-40 text-xs ${STAGE_STYLE[lead.stage] || ""}`} data-testid={`lead-stage-${lead.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{pipelineStages.map((s) => <SelectItem key={s.key} value={s.key}>{stageLabel(s.key)}</SelectItem>)}</SelectContent>
                </Select>
                <div className="relative">
                  <Calendar size={13} strokeWidth={1.5} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                  <Input
                    type="date"
                    data-testid={`lead-followup-${lead.id}`}
                    className="h-8 w-[160px] min-w-[150px] text-xs pl-7"
                    value={lead.next_follow_up_date || ""}
                    onChange={(e) => quickUpdate(lead.id, { next_follow_up_date: e.target.value })}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                <ConvertMenu lead={lead} onConvert={convert} t={t} />
                <button data-testid={`delete-lead-${lead.id}`} onClick={() => remove(lead.id)} className="lg:opacity-0 lg:group-hover:opacity-100 p-1.5 text-stone-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-opacity">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <LeadDialog open={!!dialog} lead={dialog} onClose={() => setDialog(null)} onSave={save}
                  t={t} buyers={buyers} sellers={sellers} properties={properties}
                  pipelineStages={pipelineStages} stageLabel={stageLabel} refresh={load} />
    </div>
  );
}

function LeadDialog({ open, lead, onClose, onSave, t, buyers, sellers, properties, pipelineStages, stageLabel, refresh }) {
  const [form, setForm] = useState(lead || emptyLead());
  useEffect(() => { if (lead) setForm(lead); }, [lead]);
  if (!open) return null;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Link a property to the lead (from the Estimation tab) and persist immediately
  // so the dossier — which is attached to the property — can open straight away.
  const linkProperty = async (pid, pname) => {
    const name = pname ?? (properties.find((p) => p.id === pid)?.name || "");
    setForm((f) => ({ ...f, property_id: pid, property_name: name }));
    if (form.id && pid) {
      try {
        await api.put(`/leads/${form.id}`, { property_id: pid, property_name: name });
        refresh();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
  };

  const linkedPropertyName = properties.find((p) => p.id === form.property_id)?.name || form.property_name || "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white" data-testid="lead-dialog">
        <DialogHeader><DialogTitle>{form.id ? t("editLead") : t("addLead")}</DialogTitle></DialogHeader>
        <Tabs defaultValue="details" className="mt-2">
          <TabsList className="bg-stone-100 h-auto p-1">
            <TabsTrigger value="details" data-testid="lead-tab-details">Info</TabsTrigger>
            {form.id && <TabsTrigger value="dossier" data-testid="lead-tab-dossier">{t("dossierTab")}</TabsTrigger>}
            {form.id && <TabsTrigger value="timeline" data-testid="lead-tab-timeline">{t("timelineTab")}</TabsTrigger>}
          </TabsList>
          <TabsContent value="details">
        <div className="space-y-3 mt-4">
          <div>
            <Label>{t("leadName")}</Label>
            <Input data-testid="lead-name-input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder={t("leadNamePlaceholder")} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{t("source")}</Label>
              <Select value={form.source || "website"} onValueChange={(v) => setField("source", v)}>
                <SelectTrigger data-testid="lead-source-input"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{t(`source_${s}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("stage")}</Label>
              <Select value={form.stage || "new_vendor_lead"} onValueChange={(v) => setField("stage", v)}>
                <SelectTrigger data-testid="lead-stage-input"><SelectValue /></SelectTrigger>
                <SelectContent>{pipelineStages.map((s) => <SelectItem key={s.key} value={s.key}>{stageLabel(s.key)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("priority")}</Label>
              <Select value={form.priority || "medium"} onValueChange={(v) => setField("priority", v)}>
                <SelectTrigger data-testid="lead-priority-input"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`priority_${p}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("email")}</Label>
              <Input type="email" data-testid="lead-email-input" value={form.email || ""} onChange={(e) => setField("email", e.target.value)} />
            </div>
            <div>
              <Label>{t("phone")}</Label>
              <PhoneField testId="lead-phone-input" value={form.phone} onChange={(v) => setField("phone", v)} />
            </div>
          </div>
          <div>
            <Label>{t("address")}</Label>
            <Input data-testid="lead-address-input" value={form.address || ""} onChange={(e) => setField("address", e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SelectField label={t("buyer")} value={form.buyer_id} options={buyers} onChange={(v) => setField("buyer_id", v)} testId="lead-buyer-select" t={t}
              onCreateNew={async (name) => {
                const { data } = await api.post("/buyers", { name });
                await refresh();
                setField("buyer_id", data.id);
              }} />
            <SelectField label={t("seller")} value={form.seller_id} options={sellers} onChange={(v) => setField("seller_id", v)} testId="lead-seller-select" t={t}
              onCreateNew={async (name) => {
                const { data } = await api.post("/sellers", { name });
                await refresh();
                setField("seller_id", data.id);
              }} />
            <SelectField label={t("property")} value={form.property_id} options={properties} onChange={(v) => setField("property_id", v)} testId="lead-property-select" t={t}
              onCreateNew={async (name) => {
                const { data } = await api.post("/properties", { name });
                await refresh();
                setField("property_id", data.id);
              }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("nextAction")}</Label>
              <Input data-testid="lead-nextaction-input" value={form.next_action || ""} onChange={(e) => setField("next_action", e.target.value)} />
            </div>
            <div>
              <Label>{t("nextFollowUpDate")}</Label>
              <Input type="date" data-testid="lead-followup-input" value={form.next_follow_up_date || ""} onChange={(e) => setField("next_follow_up_date", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>{t("notes")}</Label>
            <textarea rows={3} data-testid="lead-notes-input" className="w-full text-sm border border-stone-200 rounded-lg p-2" value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
          </div>
        </div>
          </TabsContent>
          {form.id && (
            <TabsContent value="dossier">
              <div className="pt-4">
                {form.property_id ? (
                  <div className="space-y-4">
                    <p className="text-xs text-stone-500" data-testid="lead-dossier-linked-property">
                      {t("dossierLinkedProperty")}: <span className="font-medium text-stone-900">{linkedPropertyName}</span>
                    </p>
                    <PropertyDossier propertyId={form.property_id} propertyName={linkedPropertyName} />
                  </div>
                ) : (
                  <div className="space-y-3 max-w-md">
                    <p className="text-sm text-stone-500" data-testid="lead-dossier-no-property">{t("dossierNeedsProperty")}</p>
                    <SelectField label={t("property")} value={form.property_id} options={properties} onChange={(v) => linkProperty(v)} testId="lead-dossier-property-select" t={t}
                      onCreateNew={async (name) => {
                        const { data } = await api.post("/properties", { name });
                        await refresh();
                        await linkProperty(data.id, data.name);
                      }} />
                  </div>
                )}
              </div>
            </TabsContent>
          )}
          {form.id && (
            <TabsContent value="timeline">
              <div className="pt-4">
                <Timeline linkKey="lead_id" linkId={form.id} linkName={form.name} />
              </div>
            </TabsContent>
          )}
        </Tabs>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} data-testid="lead-cancel">{t("cancel")}</Button>
          <Button data-testid="lead-save" onClick={() => onSave(form)} className="bg-stone-900 hover:bg-stone-800 text-white">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SelectField({ label, value, options, onChange, testId, t, onCreateNew }) {
  const handleChange = async (v) => {
    if (v === "none") return onChange("");
    if (v === "__new__" && onCreateNew) {
      const name = window.prompt(t("createNewPromptLabel").replace("{label}", label));
      if (!name || !name.trim()) return;
      try {
        await onCreateNew(name.trim());
        toast.success(t("created"));
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
      return;
    }
    onChange(v);
  };
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || "none"} onValueChange={handleChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={t("none")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t("none")}</SelectItem>
          {onCreateNew && (
            <SelectItem value="__new__" data-testid={`${testId}-create-new`}>
              + {t("createNew").replace("{label}", label.toLowerCase())}
            </SelectItem>
          )}
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.ref_code ? `${o.ref_code} · ` : ""}{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}


/* One "Convert" pill per row.
   Own open state + a setTimeout so window.confirm() fires AFTER Radix
   finishes closing the popover — that was the bug the two-button hack masked. */
function ConvertMenu({ lead, onConvert, t }) {
  const [open, setOpen] = useState(false);
  const handleChoose = (target) => {
    setOpen(false);
    setTimeout(() => onConvert(lead, target), 0);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`convert-lead-${lead.id}`}
          className="inline-flex items-center gap-1 h-8 px-2.5 text-xs text-stone-600 border border-stone-200 rounded-lg hover:border-stone-400 hover:bg-stone-50 transition-colors"
        >
          <ArrowRightLeft size={12} /> {t("convert")}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1">
        <button
          type="button"
          data-testid={`convert-to-buyer-${lead.id}`}
          onClick={() => handleChoose("buyer")}
          className="w-full flex items-center gap-2 text-left text-sm text-stone-700 hover:bg-stone-100 rounded-md px-2.5 py-2 transition-colors"
        >
          <User size={14} /> {t("convertToBuyer")}
        </button>
        <button
          type="button"
          data-testid={`convert-to-seller-${lead.id}`}
          onClick={() => handleChoose("seller")}
          className="w-full flex items-center gap-2 text-left text-sm text-stone-700 hover:bg-stone-100 rounded-md px-2.5 py-2 transition-colors"
        >
          <Users size={14} /> {t("convertToSeller")}
        </button>
      </PopoverContent>
    </Popover>
  );
}
